import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import services from '@/services/index.ts';
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import Loading from '../Loading.tsx';
import { WebsiteSchema } from '@/types/blueprint.ts';
import WebsiteSchemaEditor from './WebsiteSchemaEditor.tsx';

const WebsiteSchemaTool: React.FC = () => {
  const location = useLocation();
  const [insuranceLine, setInsuranceLine] = useState<string>('');
  const [websiteName, setWebsiteName] = useState<string>('');
  const [result, setResult] = useState<WebsiteSchema | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');

  const getWebsiteSchema = async ({
    insuranceLine,
    websiteName,
  }: {
    insuranceLine: string;
    websiteName: string;
  }): Promise<void> => {
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const params = {
        insurance_line: insuranceLine,
        website_name: websiteName,
      };
      const res = await services.extractionApis.getWebsiteSchema(params);
      if (res.err) {
        throw new Error(res.err);
      }
      const data: WebsiteSchema = res.data.website_schema;
      setResult(data);
    } catch (error) {
      setError((error as Error).message);
      toast.error('Error getting website schema');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const insuranceLineParam = params.get('insurance_line') ?? '';
    const websiteNameParam = params.get('website_name') ?? '';
    setInsuranceLine(insuranceLineParam);
    setWebsiteName(websiteNameParam);
    handleGetWebsiteSchema(insuranceLineParam, websiteNameParam);
  }, [location.search]);

  const handleGetWebsiteSchema = (
  line: string = insuranceLine,
  website: string = websiteName
): void => {
  if (!line || !website) {
    setError('Missing required parameters');
    return;
  } 
  getWebsiteSchema({
    insuranceLine: line,
    websiteName: website,
  });
};

  if (loading) {
    return <Loading />;
  }

  if(result) {
    return(
      <WebsiteSchemaEditor websiteSchema={result} />
    )
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-gray-50 p-4">
      <Card className="w-full max-w-xl">
        <CardHeader>
          <CardTitle>Website Schema Tool</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="mb-4 space-y-2">
            <div className="flex flex-col sm:flex-row sm:space-x-4">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700">
                  Insurance Line
                </label>
                <input
                  className="focus:border-primary focus:ring-primary/50 mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring"
                  onChange={(e) => setInsuranceLine(e.target.value)}
                  placeholder="Enter insurance line"
                  type="text"
                  value={insuranceLine}
                />
              </div>
              <div className="mt-2 flex-1 sm:mt-0">
                <label className="block text-sm font-medium text-gray-700">
                  Website Name
                </label>
                <input
                  className="focus:border-primary focus:ring-primary/50 mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring"
                  onChange={(e) => setWebsiteName(e.target.value)}
                  placeholder="Enter website name"
                  type="text"
                  value={websiteName}
                />
              </div>
            </div>
            <Button
              className="mt-4 w-full"
              onClick={() => handleGetWebsiteSchema(insuranceLine, websiteName)}
            >
              {loading ? 'Loading...' : 'Get Website Schema'}
            </Button>
          </div>
          {error && <div className="mb-4 text-red-500">{error}</div>}
        </CardContent>
      </Card>
    </div>
  );
};

export default WebsiteSchemaTool;
